import { getIntlLocale } from '$lib/i18n';

// Locale-aware formatters. We keep all numeric/date formatting in one place
// so the monospace/right-aligned column rule stays consistent across views.
//
// The Intl.* objects are cached per locale tag so we don't rebuild them on
// every cell render — getIntlLocale() reads from the i18next singleton
// (re-evaluated each call so a language switch reflects on the next paint).

interface CurrencyFormatters {
  dkk: Intl.NumberFormat;
  integer: Intl.NumberFormat;
  oneDecimal: Intl.NumberFormat;
  dateShort: Intl.DateTimeFormat;
  dateLong: Intl.DateTimeFormat;
  perSqmSuffix: string;
  sqmSuffix: string;
}

const cache = new Map<string, CurrencyFormatters>();

function fmts(): CurrencyFormatters {
  const tag = getIntlLocale();
  let bag = cache.get(tag);
  if (!bag) {
    bag = {
      dkk: new Intl.NumberFormat(tag, { style: 'currency', currency: 'DKK', maximumFractionDigits: 0 }),
      integer: new Intl.NumberFormat(tag, { maximumFractionDigits: 0 }),
      oneDecimal: new Intl.NumberFormat(tag, { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
      dateShort: new Intl.DateTimeFormat(tag, { year: 'numeric', month: 'short', day: '2-digit' }),
      dateLong: new Intl.DateTimeFormat(tag, {
        year: 'numeric',
        month: 'long',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      }),
      // m² and kr/m² suffixes don't change between en-DK and da-DK — they're
      // unit symbols, not translated copy. Keep them attached to the
      // formatter bag so callers don't need to import a separate helper.
      perSqmSuffix: 'kr/m²',
      sqmSuffix: 'm²'
    };
    cache.set(tag, bag);
  }
  return bag;
}

export function formatKr(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  return fmts().dkk.format(value);
}

export function formatKrPerSqm(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  const f = fmts();
  return `${f.integer.format(value)} ${f.perSqmSuffix}`;
}

export function formatSqm(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  const f = fmts();
  return `${f.oneDecimal.format(value)} ${f.sqmSuffix}`;
}

export function formatRooms(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  return fmts().oneDecimal.format(value);
}

export function formatYear(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  return fmts().integer.format(value);
}

export function formatDate(iso: string | Date | null | undefined): string {
  if (!iso) return '—';
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return '—';
  return fmts().dateShort.format(d);
}

export function formatDateLong(iso: string | Date | null | undefined): string {
  if (!iso) return '—';
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return '—';
  return fmts().dateLong.format(d);
}

export function formatNumber(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  return fmts().integer.format(value);
}
